// src/appbuilder/chat/firebase-appchat.js
// ✅ Scheme B ONLY (NO /conversations):
// userConversations/{uid}/profiles/{myProfileId}/inbox/{cid}
//   - thread doc: .../inbox/{cid}
//   - messages:   .../inbox/{cid}/messages/{mid}
//
// Single-file Firebase chat adapter (Firestore + optional RTDB presence) + DEBUG tools
//
// Exports:
//   pairCid, pairCidScoped, pairCidScopedProfile,
//   LXC_openOrCreateConversation, LXC_sendMessage, LXC_subscribeConversation,
//   LXC_setTyping, LXC_markReadInboxRow,
//   LXC_listMyConversations, LXC_archiveInbox,
//   (optional) LXC_getThreadSnapshot, LXC_debugWatchConversation,
//   public listings index: LXC_listAllListingsForApp, LXC_upsertPublicListingIndex,
//                         LXC_deletePublicListingIndex, LXC_backfillPublicListingsIndexByUids,
//   profiles(listings): LXC_listMyProfilesFromListings,
//   presence: initPresenceForUser, watchPresence
//
// Notes:
// - rid (inbox row docId) == cid (thread id) ALWAYS
// - ✅ This file strips "__p_" suffix if someone accidentally passes ridKey
// - ✅ Self-chat cross-profile writes TWO buckets (two inbox threads) so Recents works

import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  collection,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  query,
  orderBy,
  limit,
  startAfter,
  increment,
  where,
  writeBatch,
  // 2026-05-08: reactions atomic add/remove. Map field shape:
  //   reactions: { "❤️": [uid1, uid2], "👍": [uid1] }
  // arrayUnion / arrayRemove on dotted path keep concurrent writers
  // from clobbering each other's reactions.
  arrayUnion,
  arrayRemove,
  updateDoc,
} from "firebase/firestore";

import {
  getDatabase,
  ref as rRef,
  onDisconnect,
  set as rSet,
  serverTimestamp as rtdbNow,
  onValue,
} from "firebase/database";

import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref as sRef, deleteObject } from "firebase/storage";

/* ========== instances ========== */
const db = getFirestore();
const rtdb = getDatabase();
const auth = getAuth();
const storage = getStorage();

/* ========== DEBUG core ========== */
let __DEBUG__ =
  typeof window !== "undefined" &&
  (window.APPCHAT_DEBUG === true ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("APPCHAT_DEBUG") === "1"));

let __LOGGER__ = {
  log: (...a) => console.log("%c[appchat]", "color:#3b82f6", ...a),
  warn: (...a) => console.warn("%c[appchat]", "color:#f59e0b", ...a),
  error: (...a) => console.error("%c[appchat]", "color:#ef4444", ...a),
  debug: (...a) => console.debug("%c[appchat]", "color:#8b5cf6", ...a),
};

export function setAppChatDebug(v = true) {
  __DEBUG__ = !!v;
  try {
    localStorage.setItem("APPCHAT_DEBUG", v ? "1" : "0");
  } catch {}
  __LOGGER__.log("DEBUG =", __DEBUG__);
}
export function setAppChatLogger(logger) {
  if (!logger) return;
  const f = (k) =>
    typeof logger[k] === "function" ? logger[k] : __LOGGER__[k];
  __LOGGER__ = {
    log: f("log"),
    warn: f("warn"),
    error: f("error"),
    debug: f("debug"),
  };
  __LOGGER__.log("Custom logger attached.");
}
function dbg(...args) {
  if (__DEBUG__) __LOGGER__.debug(...args);
}
function info(...args) {
  if (__DEBUG__) __LOGGER__.log(...args);
}
function warn(...args) {
  if (__DEBUG__) __LOGGER__.warn(...args);
}
function err(...args) {
  __LOGGER__.error(...args);
}

/* ========== utils ========== */
const cleanStr = (v) => String(v ?? "").trim();
const clampText = (v, n) => cleanStr(v).slice(0, n);
const safeObj = (o) => (o && typeof o === "object" ? o : {});
const nowMs = () => Date.now();

/**
 * ✅ Fix B: unify rid strategy
 * If someone passes "cid__p_xxx", we force rid -> "cid".
 */
function normalizeRid(rid) {
  const r = cleanStr(rid);
  if (!r) return "";
  const i = r.indexOf("__p_");
  return i > 0 ? r.slice(0, i) : r;
}

/** 1:1 cid (legacy helper, still useful as a base) */
export const pairCid = (a, b) => {
  const arr = [cleanStr(a), cleanStr(b)].filter(Boolean);
  if (arr.length === 0) throw new Error("pairCid: uid missing");
  const uniq = Array.from(new Set(arr));
  const [x, y] = uniq.length === 1 ? [uniq[0], uniq[0]] : uniq.sort();
  return `c_${x}_${y}`;
};

/** ✅ scope cid: same two uids, different app won't mix */
export const pairCidScoped = (ownerUid, appId, a, b) => {
  const o = cleanStr(ownerUid || "o");
  const app = cleanStr(appId || "a");
  const arr = [cleanStr(a), cleanStr(b)].filter(Boolean);
  if (arr.length === 0) throw new Error("pairCidScoped: uid missing");
  const uniq = Array.from(new Set(arr));
  const [x, y] = uniq.length === 1 ? [uniq[0], uniq[0]] : uniq.sort();
  return `c_${o}_${app}_${x}_${y}`;
};

/** ✅ profile-scoped cid: same two uids, different profile pairs won't mix */
export const pairCidScopedProfile = (
  ownerUid,
  appId,
  a,
  b,
  myProfileId = "",
  peerProfileId = ""
) => {
  const o = cleanStr(ownerUid || "o");
  const app = cleanStr(appId || "a");

  const A = cleanStr(a);
  const B = cleanStr(b);
  if (!A || !B) throw new Error("pairCidScopedProfile: uid missing");

  const [x, y] = A < B ? [A, B] : [B, A];

  const p1 = cleanStr(myProfileId || "p0");
  const p2 = cleanStr(peerProfileId || "p0");
  const [pa, pb] = p1 < p2 ? [p1, p2] : [p2, p1];

  return `c_${o}_${app}_${pa}_${pb}_${x}_${y}`;
};

/** meta safe for Firestore */
function normalizeMeta(meta) {
  if (!meta || typeof meta !== "object") return null;
  const out = {
    myProfileId: clampText(meta.myProfileId, 120),
    myProfileNameCache: clampText(meta.myProfileNameCache, 80),
    myProfileAvatarCache: clampText(meta.myProfileAvatarCache, 500),

    peerProfileId: clampText(meta.peerProfileId, 120),
    peerNameCache: clampText(meta.peerNameCache, 80),
    peerAvatarCache: clampText(meta.peerAvatarCache, 500),

    peerUid: clampText(meta.peerUid || meta.targetUid || "", 120),
  };
  const hasAny = Object.values(out).some((v) => !!cleanStr(v));
  return hasAny ? out : null;
}

/** ✅ context decides "which app space" */
function normalizeContext(ctx) {
  if (!ctx || typeof ctx !== "object") return null;
  const ownerUid = clampText(ctx.ownerUid, 120);
  const appId = clampText(ctx.appId, 120);
  const templateId = clampText(ctx.templateId || "", 40);
  if (!ownerUid || !appId) return null;
  const out = { ownerUid, appId };
  if (templateId) out.templateId = templateId;
  return out;
}

function normalizeFileAttachment(file) {
  if (!file || typeof file !== "object") return null;
  const url = clampText(file.url, 2000);
  if (!url) return null;
  const out = {
    url,
    name: clampText(file.name || "file", 240),
    type: clampText(file.type || "application/octet-stream", 120),
    storagePath: clampText(file.storagePath || "", 500),
    size: Number.isFinite(Number(file.size)) ? Number(file.size) : 0,
    durationSec: Number.isFinite(Number(file.durationSec)) ? Math.max(0, Number(file.durationSec)) : 0,
  };
  return out;
}

function normalizeReplyPayload(reply) {
  if (!reply || typeof reply !== "object") return null;
  const out = {
    id: clampText(reply.id || reply.messageId || reply.replyToMessageId || "", 120),
    senderUid: clampText(reply.senderUid || reply.replyToSenderUid || "", 120),
    senderProfileId: clampText(
      reply.senderProfileId || reply.replyToSenderProfileId || "",
      120
    ),
    senderName: clampText(reply.senderName || reply.replyToSenderName || "", 80),
    kind: clampText(reply.kind || reply.replyToKind || "", 24).toLowerCase(),
    text: clampText(reply.text || reply.replyToText || "", 500),
    fileName: clampText(reply.fileName || reply.replyToFileName || "", 240),
    fileType: clampText(reply.fileType || reply.replyToFileType || "", 120).toLowerCase(),
    preview: clampText(reply.preview || reply.replyToPreview || "", 320),
  };
  const hasAny = Object.values(out).some((v) => !!cleanStr(v));
  return hasAny ? out : null;
}

function fileExt(name = "") {
  const n = cleanStr(name).toLowerCase();
  const m = n.match(/\.([a-z0-9]{2,8})(?:$|[?#])/i);
  return m ? m[1] : "";
}

function classifyFileKind(file = null) {
  const t = cleanStr(file?.type || "").toLowerCase();
  const ext = fileExt(file?.name || "");
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("image/")) return ext === "gif" ? "gif" : "image";
  if (t.startsWith("video/")) return "video";

  if (["mp3", "m4a", "wav", "aac", "flac", "oga", "opus", "amr", "ogg", "webm"].includes(ext))
    return "audio";
  if (["gif"].includes(ext)) return "gif";
  if (["png", "jpg", "jpeg", "webp", "bmp", "svg", "avif", "heic", "heif"].includes(ext))
    return "image";
  if (["mp4", "mov", "m4v", "ogv"].includes(ext)) return "video";
  return "file";
}

function buildAttachmentPreviewText(file = null) {
  const kind = classifyFileKind(file);
  if (kind === "audio") return "Voice message";
  if (kind === "gif") return "GIF";
  if (kind === "image") return "Photo";
  if (kind === "video") return "Video";
  const n = cleanStr(file?.name || "");
  return n ? `Attachment: ${n.slice(0, 160)}` : "File attachment";
}

/* =========================================================
   ✅ Scheme B helpers (ONE TRUE PATH)
   userConversations/{uid}/profiles/{myProfileId}/inbox/{cid}
   ========================================================= */
function inboxCol(uid, myProfileId) {
  const u = cleanStr(uid);
  const p = cleanStr(myProfileId);
  if (!u || !p) throw new Error("inboxCol: uid/myProfileId missing");
  return collection(db, "userConversations", u, "profiles", p, "inbox");
}
function threadRef(uid, myProfileId, cid) {
  const u = cleanStr(uid);
  const p = cleanStr(myProfileId);
  const c = normalizeRid(cid);
  if (!u || !p || !c) throw new Error("threadRef: uid/myProfileId/cid missing");
  return doc(db, "userConversations", u, "profiles", p, "inbox", c);
}
function messagesCol(uid, myProfileId, cid) {
  return collection(threadRef(uid, myProfileId, cid), "messages");
}
function makeClientId() {
  // Just generate a Firestore-style random id without writing anything.
  return doc(collection(db, "__ids__")).id;
}

/* =========================================================
   ✅ A) public_listings index
   apps/{appId}/public_listings/{docId}
   ========================================================= */
function publicListingDocId(ownerUid, listingId) {
  return `${cleanStr(ownerUid)}_${cleanStr(listingId)}`.slice(0, 300);
}

export async function LXC_upsertPublicListingIndex({
  appId,
  ownerUid,
  listingId,
  name = "",
  title = "",
  avatarUrl = "",
  extra = null,
} = {}) {
  const a = cleanStr(appId);
  const o = cleanStr(ownerUid);
  const lid = cleanStr(listingId);
  if (!a || !o || !lid)
    throw new Error(
      "upsertPublicListingIndex: missing appId/ownerUid/listingId"
    );

  const docId = publicListingDocId(o, lid);
  const ref = doc(db, "apps", a, "public_listings", docId);

  const payload = {
    appId: a,
    ownerUid: o,
    listingId: lid,
    name: clampText(name || title, 120),
    title: clampText(title || name, 120),
    avatarUrl: clampText(avatarUrl, 800),
    updatedAt: serverTimestamp(),
    updatedAtMs: nowMs(),
    ...(extra && typeof extra === "object"
      ? { extra: safeObj(extra) }
      : {}),
  };

  await setDoc(ref, payload, { merge: true });
  dbg("[public_listings][upsert]", {
    appId: a,
    docId,
    ownerUid: o,
    listingId: lid,
  });
  return { docId, ...payload };
}

export async function LXC_deletePublicListingIndex({
  appId,
  ownerUid,
  listingId,
} = {}) {
  const a = cleanStr(appId);
  const o = cleanStr(ownerUid);
  const lid = cleanStr(listingId);
  if (!a || !o || !lid) return false;

  const docId = publicListingDocId(o, lid);
  const ref = doc(db, "apps", a, "public_listings", docId);
  await deleteDoc(ref);
  dbg("[public_listings][delete]", { appId: a, docId });
  return true;
}

export async function LXC_listAllListingsForApp({ appId, limitN = 8000 } = {}) {
  const a = cleanStr(appId);
  if (!a) return [];

  const colRef = collection(db, "apps", a, "public_listings");
  const q1 = query(colRef, limit(Math.max(1, Math.min(limitN, 8000))));
  const snap = await getDocs(q1);

  const out = snap.docs
    .map((d) => {
      const x = d.data() || {};
      return {
        id: d.id,
        ownerUid: cleanStr(x.ownerUid || x.uid),
        listingId: cleanStr(x.listingId || x.profileId || x.id),
        title: cleanStr(x.name || x.title),
        name: cleanStr(x.name || x.title),
        avatarUrl: cleanStr(x.avatarUrl || ""),
        updatedSort: x?.updatedAtMs || 0,
      };
    })
    .filter((r) => r.ownerUid && r.listingId);

  out.sort((a1, b1) => (a1.title || "").localeCompare(b1.title || ""));
  dbg("[public_listings][list]", { appId: a, count: out.length });
  return out;
}

export async function LXC_backfillPublicListingsIndexByUids({
  appId,
  uids = [],
  perUserLimit = 2000,
  onProgress = null,
} = {}) {
  const a = cleanStr(appId);
  if (!a) throw new Error("backfill: missing appId");
  if (!Array.isArray(uids) || uids.length === 0)
    return { users: 0, listings: 0 };

  let total = 0;
  let okUsers = 0;

  for (let i = 0; i < uids.length; i++) {
    const uid = cleanStr(uids[i]);
    if (!uid) continue;

    try {
      const rows = await LXC_listMyProfilesFromListings({
        ownerUid: uid,
        appId: a,
        authorUid: uid,
        limitN: perUserLimit,
      });

      for (const p of rows || []) {
        await LXC_upsertPublicListingIndex({
          appId: a,
          ownerUid: uid,
          listingId: p.id,
          name: p?.name || p?.title || "",
          title: p?.title || p?.name || "",
          avatarUrl: p?.avatarUrl || "",
          extra: { source: "backfill" },
        });
        total++;
      }

      okUsers++;
      onProgress &&
        onProgress({
          i: i + 1,
          totalUsers: uids.length,
          uid,
          added: (rows || []).length,
          total,
        });

      dbg("[backfill] user done", { uid, added: (rows || []).length, total });
    } catch (e) {
      err("[backfill] user failed", uid, e?.message || e);
      onProgress &&
        onProgress({
          i: i + 1,
          totalUsers: uids.length,
          uid,
          error: String(e?.message || e),
        });
    }
  }

  info("[backfill] done", { appId: a, users: okUsers, listings: total });
  return { users: okUsers, listings: total };
}

/* ========== profiles(listings) ========== */
export async function LXC_listMyProfilesFromListings({
  ownerUid,
  appId,
  authorUid,
  limitN = 1000,
} = {}) {
  const o = cleanStr(ownerUid);
  const a = cleanStr(appId);
  const au = cleanStr(authorUid);
  if (!o || !a || !au) return [];

  const base = collection(db, "users", o, "apps", a, "listings");
  const q1 = query(
    base,
    where("authorUid", "==", au),
    limit(Math.max(1, Math.min(limitN, 5000)))
  );
  const snap = await getDocs(q1);

  const out = snap.docs
    .map((d) => {
      const root = d.data() || {};
      const data = root?.data || {};
      const avatarUrl = Array.isArray(data.photos) ? data.photos[0] || "" : "";
      return {
        id: d.id,
        data,
        title: cleanStr(data.title || data.name || ""),
        name: cleanStr(data.name || data.title || ""),
        avatarUrl: cleanStr(
          avatarUrl || data.avatarUrl || data.photoUrl || data.photoURL || ""
        ),
        updatedSort:
          data?.updatedAt ||
          root?.updatedAt ||
          data?.lastActiveAt ||
          root?.createdAt ||
          0,
      };
    })
    .filter((x) => !!x.id);

  out.sort((a1, b1) => {
    const A = a1.updatedSort || 0;
    const B = b1.updatedSort || 0;
    if (A === B) return 0;
    return A > B ? -1 : 1;
  });

  return out;
}

/* =========================================================
   ✅ 1) open or create (Scheme B ONLY)
   Ensures inbox thread doc exists in BOTH buckets (fan-out)
   ========================================================= */
export async function LXC_openOrCreateConversation(myUid, targetUid, opts = {}) {
  const me = cleanStr(myUid);
  const peer = cleanStr(targetUid);
  if (!me || !peer) throw new Error("openOrCreateConversation: uid missing");

  const ctx = normalizeContext(opts?.context);
  const metaN = normalizeMeta(opts?.meta || opts);

  const myPid = cleanStr(metaN?.myProfileId || "");
  const peerPid = cleanStr(metaN?.peerProfileId || "");
  if (!myPid) throw new Error("openOrCreateConversation: myProfileId missing");
  if (!peerPid)
    throw new Error("openOrCreateConversation: peerProfileId missing");

  const hasProfile = !!myPid && !!peerPid;
  const cid = ctx
    ? hasProfile
      ? pairCidScopedProfile(ctx.ownerUid, ctx.appId, me, peer, myPid, peerPid)
      : pairCidScoped(ctx.ownerUid, ctx.appId, me, peer)
    : pairCid(me, peer);

  const c = normalizeRid(cid);

  const isSelfChat = me === peer;
  const isCrossProfile = isSelfChat && myPid !== peerPid;

  // Targets:
  // - normal: (me,myPid) & (peer,peerPid)
  // - self cross-profile: (me,myPid) & (me,peerPid)
  const targets = isCrossProfile
    ? [
        {
          uid: me,
          bucketPid: myPid,
          peerUid: me,
          peerPid: peerPid,
          isSenderView: true,
        },
        {
          uid: me,
          bucketPid: peerPid,
          peerUid: me,
          peerPid: myPid,
          isSenderView: false,
        },
      ]
    : [
        {
          uid: me,
          bucketPid: myPid,
          peerUid: peer,
          peerPid: peerPid,
          isSenderView: true,
        },
        {
          uid: peer,
          bucketPid: peerPid,
          peerUid: me,
          peerPid: myPid,
          isSenderView: false,
        },
      ];

  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();

  await Promise.all(
    targets.map(async (t) => {
      const ref = threadRef(t.uid, t.bucketPid, c);

      // For this user's viewpoint, the "peer caches" should reflect the other profile
      const peerNameCache = cleanStr(
        t.isSenderView ? metaN?.peerNameCache : metaN?.myProfileNameCache
      );
      const peerAvatarCache = cleanStr(
        t.isSenderView ? metaN?.peerAvatarCache : metaN?.myProfileAvatarCache
      );

      const payload = {
        cid: c,
        rid: c,
        inboxId: c,

        // viewpoint fields
        myProfileId: cleanStr(t.bucketPid),
        peerUid: cleanStr(t.peerUid),
        peerProfileId: cleanStr(t.peerPid),

        peerNameCache,
        peerAvatarCache,

        archived: false,
        unreadCount: 0,

        lastText: "",
        lastKind: "text",
        lastFileName: "",
        lastFileType: "",
        lastFileDurationSec: 0,
        lastAt: null,

        updatedAt: serverTimestamp(),
        updatedAtMs: nowMs(),

        ...(ctx
          ? {
              scopeOwnerUid: ctx.ownerUid,
              scopeAppId: ctx.appId,
              ownerUid: ctx.ownerUid,
              appId: ctx.appId,
              ...(ctx.templateId ? { templateId: ctx.templateId } : {}),
            }
          : {}),
      };

      await setDoc(ref, payload, { merge: true });
    })
  );

  const dt =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
  info("[openOrCreate][schemeB] done", {
    cid: c,
    ms: +dt.toFixed(1),
    targets: targets.map((x) => ({
      uid: x.uid,
      bucketPid: x.bucketPid,
      peerUid: x.peerUid,
    })),
  });

  return c;
}

/* =========================================================
   ✅ 2) send message (Scheme B ONLY)
   Fan-out: write message doc into BOTH buckets + update both thread docs
   ========================================================= */
export async function LXC_sendMessage(cid, payload = {}) {
  const c = normalizeRid(cid);
  if (!c) throw new Error("sendMessage: cid missing");

  const {
    text,
    from,
    to = null,
    meta = null,
    reply = null,
    context = null,
    file = null,
    ...flatMaybeMeta
  } = payload || {};

  const fromUid = cleanStr(from);
  const toUid = cleanStr(to || "");
  if (!fromUid) throw new Error("sendMessage: from missing");

  const ctx = normalizeContext(context);
  const metaN = normalizeMeta(meta || flatMaybeMeta);
  const fileN = normalizeFileAttachment(file || flatMaybeMeta?.file);
  const replyN = normalizeReplyPayload(reply || flatMaybeMeta?.reply);

  const myPid = cleanStr(metaN?.myProfileId || "");
  const peerPid = cleanStr(metaN?.peerProfileId || "");
  if (!myPid) throw new Error("sendMessage: myProfileId missing");
  if (!peerPid) throw new Error("sendMessage: peerProfileId missing");

  const safeText = String(text || "").slice(0, 4000);
  if (!safeText && !fileN) throw new Error("sendMessage: text or file required");
  const mid = makeClientId();
  const nextLastKind = safeText ? "text" : classifyFileKind(fileN);
  const nextLastText = safeText ? safeText.slice(0, 200) : buildAttachmentPreviewText(fileN);
  const nextLastFileName = safeText ? "" : cleanStr(fileN?.name || "").slice(0, 240);
  const nextLastFileType = safeText ? "" : cleanStr(fileN?.type || "").slice(0, 120);
  const nextLastFileDurationSec = safeText
    ? 0
    : Number.isFinite(Number(fileN?.durationSec))
      ? Math.max(0, Number(fileN?.durationSec))
      : 0;

  const isSelfChat = !!toUid && fromUid === toUid;
  const isCrossProfile = isSelfChat && myPid !== peerPid;

  // Targets (same logic as openOrCreate)
  const targets = isCrossProfile
    ? [
        {
          uid: fromUid,
          bucketPid: myPid,
          peerUid: fromUid,
          peerPid: peerPid,
          isSenderView: true,
        },
        {
          uid: fromUid,
          bucketPid: peerPid,
          peerUid: fromUid,
          peerPid: myPid,
          isSenderView: false,
        },
      ]
    : [
        {
          uid: fromUid,
          bucketPid: myPid,
          peerUid: toUid,
          peerPid: peerPid,
          isSenderView: true,
        },
        {
          uid: toUid,
          bucketPid: peerPid,
          peerUid: fromUid,
          peerPid: myPid,
          isSenderView: false,
        },
      ];

  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();

  await runTransaction(db, async (tx) => {
    for (const t of targets) {
      if (!t.uid || !t.bucketPid) continue;

      const tRef = threadRef(t.uid, t.bucketPid, c);
      const mRef = doc(messagesCol(t.uid, t.bucketPid, c), mid);
      const peerNameCache = cleanStr(
        t.isSenderView ? metaN?.peerNameCache : metaN?.myProfileNameCache
      );
      const peerAvatarCache = cleanStr(
        t.isSenderView ? metaN?.peerAvatarCache : metaN?.myProfileAvatarCache
      );

      // ensure thread doc exists (merge)
      tx.set(
        tRef,
        {
          cid: c,
          rid: c,
          inboxId: c,
          myProfileId: cleanStr(t.bucketPid),
          peerUid: cleanStr(t.peerUid),
          peerProfileId: cleanStr(t.peerPid),
          peerNameCache,
          peerAvatarCache,

          ...(ctx
            ? {
                scopeOwnerUid: ctx.ownerUid,
                scopeAppId: ctx.appId,
                ownerUid: ctx.ownerUid,
                appId: ctx.appId,
                ...(ctx.templateId ? { templateId: ctx.templateId } : {}),
              }
            : {}),

          archived: false,
          updatedAt: serverTimestamp(),
          updatedAtMs: nowMs(),
        },
        { merge: true }
      );

      // write message into THIS bucket's messages
      tx.set(
        mRef,
        {
          id: mid,
          cid: c,
          text: safeText,
          ...(replyN ? { reply: replyN } : {}),
          ...(fileN
            ? {
                fileUrl: fileN.url,
                fileName: fileN.name,
                fileType: fileN.type,
                fileSize: Number(fileN.size || 0),
                fileStoragePath: fileN.storagePath || "",
                fileDurationSec: Number(fileN.durationSec || 0),
              }
            : {}),
          createdAt: serverTimestamp(),
          createdAtMs: nowMs(),

          senderUid: fromUid,
          senderProfileId: myPid,

          // optional for debugging/UI
          toUid: toUid || null,
          toProfileId: peerPid || null,
        },
        { merge: true }
      );

      // update thread head + unread
      tx.set(
        tRef,
        {
          lastText: nextLastText,
          lastKind: nextLastKind,
          lastFileName: nextLastFileName,
          lastFileType: nextLastFileType,
          lastFileDurationSec: nextLastFileDurationSec,
          lastSenderUid: fromUid,
          lastSenderProfileId: myPid,
          lastSenderNameCache: cleanStr(metaN?.myProfileNameCache || ""),
          lastSenderAvatarCache: cleanStr(metaN?.myProfileAvatarCache || ""),
          lastAt: serverTimestamp(),
          lastAtMs: nowMs(),
          updatedAt: serverTimestamp(),
          updatedAtMs: nowMs(),

          unreadCount: t.isSenderView ? 0 : increment(1),
        },
        { merge: true }
      );
    }
  });

  const dt =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
  info("[sendMessage][schemeB] committed", {
    cid: c,
    mid,
    ms: +dt.toFixed(1),
    from: fromUid,
    to: toUid,
    targets: targets.map((x) => ({
      uid: x.uid,
      bucketPid: x.bucketPid,
      isSenderView: x.isSenderView,
    })),
  });

  return { cid: c, mid };
}

/* =========================================================
   ✅ 3) subscribe messages + typing (Scheme B ONLY)
   signature: LXC_subscribeConversation({ uid, myProfileId, cid }, setState, opts?)
   ========================================================= */
export function LXC_subscribeConversation(
  arg1,
  setState,
  { pageSize = 200, after = null } = {}
) {
  // Accept ONLY the new signature, but guard hard to avoid "n.indexOf is not a function"
  if (!arg1 || typeof arg1 !== "object") {
    warn("[subscribe][schemeB] bad args", { arg1Type: typeof arg1 });
    return () => {};
  }

  const uid = cleanStr(arg1.uid);
  const myProfileId = cleanStr(arg1.myProfileId);
  const cid = normalizeRid(arg1.cid);

  if (!uid || !myProfileId || !cid || typeof setState !== "function") {
    warn("[subscribe][schemeB] missing uid/pid/cid or setState", {
      uid,
      myProfileId,
      cid,
    });
    return () => {};
  }

  const col = messagesCol(uid, myProfileId, cid);

  // messages query
  const qMsgs = after
    ? query(
        col,
        orderBy("createdAt", "desc"),
        orderBy("__name__", "desc"),
        startAfter(after),
        limit(Math.max(1, Math.min(pageSize, 500)))
      )
    : query(
        col,
        orderBy("createdAt", "desc"),
        orderBy("__name__", "desc"),
        limit(Math.max(1, Math.min(pageSize, 500)))
      );

  const unMsg = onSnapshot(
    qMsgs,
    (snap) => {
      const messages = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .reverse();
      setState((prev) => ({ ...(prev || {}), messages }));
      dbg("[subscribe][schemeB][messages]", {
        cid,
        uid,
        myProfileId,
        count: messages.length,
      });
    },
    (e) => err("[subscribe][schemeB][messages] error", e?.message || e)
  );

  // typing is stored on thread doc: typing.{uid}=true/false
  const tRef = threadRef(uid, myProfileId, cid);
  const unTyping = onSnapshot(
    tRef,
    (snap) => {
      const d = snap.data() || {};
      const typingRaw =
        d.typing && typeof d.typing === "object" ? d.typing : {};
      const typing = {};
      Object.keys(typingRaw).forEach((k) => {
        if (typingRaw[k]) typing[k] = true;
      });
      setState((prev) => ({ ...(prev || {}), typing }));
      dbg("[subscribe][schemeB][typing]", {
        cid,
        uid,
        myProfileId,
        typing: Object.keys(typing),
      });
    },
    (e) => err("[subscribe][schemeB][typing] error", e?.message || e)
  );

  info("[subscribe][schemeB] attached", {
    cid,
    uid,
    myProfileId,
    pageSize,
    after: !!after,
  });

  return () => {
    try {
      unMsg();
    } catch {}
    try {
      unTyping();
    } catch {}
    info("[subscribe][schemeB] detached", { cid, uid, myProfileId });
  };
}

/* =========================================================
   ✅ 4) typing (Scheme B ONLY)
   signature: LXC_setTyping({ uid, myProfileId, cid }, isTyping)
   ========================================================= */
export async function LXC_setTyping(arg1, val) {
  if (!arg1 || typeof arg1 !== "object") return;
  const uid = cleanStr(arg1.uid);
  const myProfileId = cleanStr(arg1.myProfileId);
  const cid = normalizeRid(arg1.cid);
  if (!uid || !myProfileId || !cid) return;

  const ref = threadRef(uid, myProfileId, cid);

  // Use dot-path so we don't overwrite other keys
  await setDoc(
    ref,
    {
      [`typing.${uid}`]: !!val,
      typingAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedAtMs: nowMs(),
    },
    { merge: true }
  );

  dbg("[typing][schemeB]", { cid, uid, myProfileId, val: !!val });
}

/* =========================================================
   ✅ 5) mark read for ONE inbox row (profile bucket)
   ========================================================= */
export async function LXC_markReadInboxRow(uid, myProfileId, rid) {
  const u = cleanStr(uid);
  const p = cleanStr(myProfileId);
  const c = normalizeRid(rid);
  if (!u || !p || !c) return;

  await setDoc(
    threadRef(u, p, c),
    {
      unreadCount: 0,
      lastReadAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedAtMs: nowMs(),
    },
    { merge: true }
  );

  dbg("[markReadInboxRow][schemeB]", { uid: u, myProfileId: p, cid: c });
}

/* =========================================================
   ✅ 5c) reactions — atomic add/remove using arrayUnion/Remove.
   Schema: messages/{mid}.reactions = { [emoji]: [uid, uid, ...] }
   We mirror the write to BOTH viewer + peer buckets so the
   reaction is visible from either side. Group reactions go to
   appGroups/{gid}/messages/{mid} via LXC_toggleGroupReaction in
   firebase-appgroup.js — same field name.
   ========================================================= */
export async function LXC_toggleReaction({
  uid,
  myProfileId,
  cid,
  messageId,
  emoji,
  peerUid = "",
  peerProfileId = "",
  add = true,
} = {}) {
  const u = cleanStr(uid);
  const myPid = cleanStr(myProfileId);
  const c = normalizeRid(cid);
  const mid = cleanStr(messageId);
  const e = String(emoji || "").trim();
  const peer = cleanStr(peerUid);
  const peerPid = cleanStr(peerProfileId);
  if (!u || !myPid || !c || !mid || !e) return false;

  const op = add ? arrayUnion(u) : arrayRemove(u);
  const path = `reactions.${e}`;

  const targets = [
    { uid: u, bucketPid: myPid },
    ...(peer && peerPid ? [{ uid: peer, bucketPid: peerPid }] : []),
  ];

  await Promise.all(
    targets.map(async (t) => {
      const ref = doc(messagesCol(t.uid, t.bucketPid, c), mid);
      try {
        await updateDoc(ref, { [path]: op });
      } catch (err) {
        // Doc may not have a `reactions` map yet — fall back to setDoc merge.
        try {
          await setDoc(
            ref,
            { reactions: { [e]: add ? [u] : [] } },
            { merge: true }
          );
        } catch (err2) {
          warn("[toggleReaction] failed", { uid: t.uid, err: err2?.message || err2 });
        }
      }
    })
  );
  dbg("[toggleReaction]", { mid, emoji: e, add });
  return true;
}

/* =========================================================
   ✅ 5e) pin / unpin message — adds `pinned: bool, pinnedAtMs,
   pinnedBy` so a thread head can render a pinned bar. Mirrored
   to both buckets so either side sees the same pin state.
   ========================================================= */
export async function LXC_togglePinMessage({
  uid,
  myProfileId,
  cid,
  messageId,
  peerUid = "",
  peerProfileId = "",
  pin = true,
} = {}) {
  const u = cleanStr(uid);
  const myPid = cleanStr(myProfileId);
  const c = normalizeRid(cid);
  const mid = cleanStr(messageId);
  const peer = cleanStr(peerUid);
  const peerPid = cleanStr(peerProfileId);
  if (!u || !myPid || !c || !mid) return false;

  const patch = pin
    ? { pinned: true, pinnedAtMs: Date.now(), pinnedBy: u, pinnedAt: serverTimestamp() }
    : { pinned: false, pinnedAtMs: 0, pinnedBy: "" };

  const targets = [
    { uid: u, bucketPid: myPid },
    ...(peer && peerPid ? [{ uid: peer, bucketPid: peerPid }] : []),
  ];

  await Promise.all(
    targets.map(async (t) => {
      const ref = doc(messagesCol(t.uid, t.bucketPid, c), mid);
      try {
        await setDoc(ref, patch, { merge: true });
      } catch (err) {
        warn("[togglePinMessage] write failed", { uid: t.uid, err: err?.message || err });
      }
    })
  );
  dbg("[togglePinMessage]", { mid, pin });
  return true;
}

/* =========================================================
   ✅ 5f) attach a fetched OG link-preview to an existing message.
   Soft merge — caller (or server) supplies the parsed metadata,
   we just persist it on the message so future loads don't refetch.
   ========================================================= */
export async function LXC_attachLinkPreview({
  uid,
  myProfileId,
  cid,
  messageId,
  peerUid = "",
  peerProfileId = "",
  preview,
} = {}) {
  const u = cleanStr(uid);
  const myPid = cleanStr(myProfileId);
  const c = normalizeRid(cid);
  const mid = cleanStr(messageId);
  const peer = cleanStr(peerUid);
  const peerPid = cleanStr(peerProfileId);
  if (!u || !myPid || !c || !mid || !preview || typeof preview !== "object") return false;
  const safe = {
    url: String(preview.url || "").slice(0, 800),
    title: String(preview.title || "").slice(0, 280),
    description: String(preview.description || "").slice(0, 600),
    image: String(preview.image || "").slice(0, 800),
    siteName: String(preview.siteName || "").slice(0, 120),
    favicon: String(preview.favicon || "").slice(0, 800),
    fetchedAtMs: Date.now(),
  };
  const targets = [
    { uid: u, bucketPid: myPid },
    ...(peer && peerPid ? [{ uid: peer, bucketPid: peerPid }] : []),
  ];
  await Promise.all(
    targets.map(async (t) => {
      const ref = doc(messagesCol(t.uid, t.bucketPid, c), mid);
      try {
        await setDoc(ref, { linkPreview: safe }, { merge: true });
      } catch (err) {
        warn("[attachLinkPreview] write failed", { uid: t.uid, err: err?.message || err });
      }
    })
  );
  return true;
}

/* =========================================================
   ✅ 5d) edit message — replaces text + sets edited:true. Soft
   write, mirrored to both buckets like delete/recall do.
   ========================================================= */
export async function LXC_editMessage({
  uid,
  myProfileId,
  cid,
  messageId,
  text,
  peerUid = "",
  peerProfileId = "",
} = {}) {
  const u = cleanStr(uid);
  const myPid = cleanStr(myProfileId);
  const c = normalizeRid(cid);
  const mid = cleanStr(messageId);
  const peer = cleanStr(peerUid);
  const peerPid = cleanStr(peerProfileId);
  const newText = String(text || "").trim().slice(0, 4000);
  if (!u || !myPid || !c || !mid || !newText) return false;

  const patch = {
    text: newText,
    edited: true,
    editedAt: serverTimestamp(),
    editedAtMs: Date.now(),
    updatedAt: serverTimestamp(),
    updatedAtMs: Date.now(),
  };

  const targets = [
    { uid: u, bucketPid: myPid },
    ...(peer && peerPid ? [{ uid: peer, bucketPid: peerPid }] : []),
  ];

  await Promise.all(
    targets.map(async (t) => {
      const ref = doc(messagesCol(t.uid, t.bucketPid, c), mid);
      try {
        await setDoc(ref, patch, { merge: true });
      } catch (err) {
        warn("[editMessage] write failed", { uid: t.uid, err: err?.message || err });
      }
    })
  );
  dbg("[editMessage]", { mid, length: newText.length });
  return true;
}

/* =========================================================
   ✅ 5b) delete message (Scheme B buckets + optional storage cleanup)
   ========================================================= */
export async function LXC_deleteMessage({
  uid,
  myProfileId,
  cid,
  messageId,
  peerUid = "",
  peerProfileId = "",
  fileStoragePath = "",
} = {}) {
  const u = cleanStr(uid);
  const myPid = cleanStr(myProfileId);
  const c = normalizeRid(cid);
  const mid = cleanStr(messageId);
  const peer = cleanStr(peerUid);
  const peerPid = cleanStr(peerProfileId);
  const storagePath = cleanStr(fileStoragePath);

  if (!u || !myPid || !c || !mid) return false;

  const isSelfChat = !!peer && peer === u;
  const isCrossProfile = isSelfChat && !!peerPid && peerPid !== myPid;
  const targets = isCrossProfile
    ? [
        { uid: u, bucketPid: myPid },
        { uid: u, bucketPid: peerPid },
      ]
    : [
        { uid: u, bucketPid: myPid },
        ...(peer && peerPid ? [{ uid: peer, bucketPid: peerPid }] : []),
      ];

  const batch = writeBatch(db);
  for (const t of targets) {
    if (!t?.uid || !t?.bucketPid) continue;
    const msgRef = doc(messagesCol(t.uid, t.bucketPid, c), mid);
    batch.delete(msgRef);
  }
  await batch.commit();

  if (storagePath) {
    try {
      await deleteObject(sRef(storage, storagePath));
    } catch (e) {
      warn("[deleteMessage][storage] delete failed", { storagePath, err: e?.message || e });
    }
  }

  info("[deleteMessage][schemeB] done", {
    cid: c,
    messageId: mid,
    targets: targets.map((x) => ({ uid: x.uid, bucketPid: x.bucketPid })),
    storagePath: !!storagePath,
  });
  return true;
}

// Back-compat alias (some old callers used LXC_markRead)
export const LXC_markRead = async (...args) => {
  // allow: (uid, myProfileId, rid)
  return LXC_markReadInboxRow(args[0], args[1], args[2]);
};

/* =========================================================
   ✅ 6) list conversations (watch profile bucket)
   ========================================================= */
export function LXC_listMyConversations(
  uid,
  { myProfileId = "", limitN = 50, onChange, scopeOwnerUid = "", scopeAppId = "" } = {}
) {
  const u = cleanStr(uid);
  const p = cleanStr(myProfileId);
  if (!u || !p) return () => {};

  const col = inboxCol(u, p);

  const so = cleanStr(scopeOwnerUid);
  const sa = cleanStr(scopeAppId);
  const hasScope = !!so && !!sa;

  const qInbox = hasScope
    ? query(
        col,
        where("archived", "==", false),
        where("scopeOwnerUid", "==", so),
        where("scopeAppId", "==", sa),
        orderBy("updatedAt", "desc"),
        limit(Math.max(1, Math.min(limitN, 200)))
      )
    : query(
        col,
        where("archived", "==", false),
        orderBy("updatedAt", "desc"),
        limit(Math.max(1, Math.min(limitN, 200)))
      );

  info("[inbox][watch][schemeB]", { uid: u, myProfileId: p, hasScope, so, sa, limitN });

  return onSnapshot(
    qInbox,
    (snap) => {
      const rows = snap.docs.map((d) => {
        const x = d.data() || {};
        return {
          id: d.id,
          cid: cleanStr(x.cid || d.id),
          rid: cleanStr(x.rid || d.id),
          inboxId: cleanStr(x.inboxId || d.id),

          myProfileId: cleanStr(x.myProfileId || p),
          peerUid: cleanStr(x.peerUid || ""),
          peerProfileId: cleanStr(x.peerProfileId || ""),

          peerNameCache: cleanStr(x.peerNameCache || ""),
          peerAvatarCache: cleanStr(x.peerAvatarCache || ""),

          lastText: cleanStr(x.lastText || ""),
          lastKind: cleanStr(x.lastKind || "text"),
          lastFileName: cleanStr(x.lastFileName || ""),
          lastFileType: cleanStr(x.lastFileType || ""),
          lastFileDurationSec: Number(x.lastFileDurationSec || 0),
          lastAt: x.lastAt || null,
          lastAtMs: x.lastAtMs || 0,

          unreadCount: Number(x.unreadCount ?? 0),
          archived: !!x.archived,

          updatedAt: x.updatedAt || null,
          updatedAtMs: x.updatedAtMs || 0,

          scopeOwnerUid: cleanStr(x.scopeOwnerUid || ""),
          scopeAppId: cleanStr(x.scopeAppId || ""),
        };
      });

      dbg("[inbox][rows][schemeB]", {
        uid: u,
        myProfileId: p,
        count: rows.length,
        sample: rows.slice(0, 3).map((r) => ({
          id: r.id,
          cid: r.cid,
          unreadCount: r.unreadCount,
          peerUid: r.peerUid,
          peerProfileId: r.peerProfileId,
        })),
      });

      onChange && onChange(rows);
    },
    (e) => err("[inbox][schemeB] error", e?.message || e)
  );
}

/* =========================================================
   ✅ 7) archive inbox rows (profile bucket)
   ========================================================= */
export async function LXC_archiveInbox(uid, myProfileId, rids = []) {
  const u = cleanStr(uid);
  const p = cleanStr(myProfileId);
  if (!u || !p || !Array.isArray(rids) || rids.length === 0) return;

  info("[inbox][archive][schemeB]", { uid: u, myProfileId: p, count: rids.length });

  const batch = writeBatch(db);
  for (const rid of rids) {
    const c = normalizeRid(rid);
    if (!c) continue;
    batch.set(
      threadRef(u, p, c),
      { archived: true, updatedAt: serverTimestamp(), updatedAtMs: nowMs() },
      { merge: true }
    );
  }
  await batch.commit();
  dbg("[inbox][archived][schemeB]", { uid: u, myProfileId: p, count: rids.length });
}

/* =========================================================
   ✅ 8) thread snapshot (Scheme B)
   ========================================================= */
export async function LXC_getThreadSnapshot({ uid, myProfileId, cid, limitN = 50 } = {}) {
  const u = cleanStr(uid);
  const p = cleanStr(myProfileId);
  const c = normalizeRid(cid);
  if (!u || !p || !c) return { messages: [], typing: {}, thread: null };

  const mcol = messagesCol(u, p, c);
  const qMsgs = query(
    mcol,
    orderBy("createdAt", "desc"),
    orderBy("__name__", "desc"),
    limit(Math.max(1, Math.min(limitN, 500)))
  );

  const snap = await getDocs(qMsgs);
  const messages = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();

  const tsnap = await getDoc(threadRef(u, p, c));
  const tdata = tsnap.exists() ? tsnap.data() || {} : null;
  const typingRaw = tdata?.typing && typeof tdata.typing === "object" ? tdata.typing : {};
  const typing = {};
  Object.keys(typingRaw).forEach((k) => {
    if (typingRaw[k]) typing[k] = true;
  });

  info("[snapshot][schemeB]", {
    cid: c,
    uid: u,
    myProfileId: p,
    messages: messages.length,
    typing: Object.keys(typing),
  });
  return { messages, typing, thread: tdata };
}

export function LXC_debugWatchConversation({ uid, myProfileId, cid } = {}) {
  const off = LXC_subscribeConversation(
    { uid: cleanStr(uid), myProfileId: cleanStr(myProfileId), cid: normalizeRid(cid) },
    (updater) => {
      try {
        const prev = { messages: [], typing: {} };
        const next = typeof updater === "function" ? updater(prev) : updater;
        dbg("[watch][schemeB][messages]", next.messages?.length || 0);
        dbg("[watch][schemeB][typing]", Object.keys(next.typing || {}));
      } catch (e) {
        err("[watch][schemeB] state error", e?.message || e);
      }
    },
    { pageSize: 200 }
  );

  info("[watch][schemeB] attached", {
    uid: cleanStr(uid),
    myProfileId: cleanStr(myProfileId),
    cid: normalizeRid(cid),
  });
  return off;
}

/* =========================================================
   ✅ 9) presence (RTDB)
   ========================================================= */
export function initPresenceForUser() {
  onAuthStateChanged(auth, (user) => {
    if (!user?.uid) return;

    const uid = cleanStr(user.uid);
    const sref = rRef(rtdb, `status/${uid}`);

    rSet(sref, { state: "online", last_changed: rtdbNow() })
      .then(() => dbg("[presence] online set", { uid }))
      .catch((e) => err("[presence] set error", e?.message || e));

    try {
      onDisconnect(sref).set({ state: "offline", last_changed: rtdbNow() });
    } catch (e) {
      warn("[presence] onDisconnect failed", e?.message || e);
    }
  });
}

export function watchPresence(uid, cb) {
  const u = cleanStr(uid);
  if (!u) return () => {};
  const sref = rRef(rtdb, `status/${u}`);
  info("[presence] watch", { uid: u });

  return onValue(
    sref,
    (snap) => {
      const v = snap.val() || { state: "offline" };
      dbg("[presence]", { uid: u, ...v });
      cb && cb(v);
    },
    (e) => err("[presence] error", e?.message || e)
  );
}

/* =========================================================
   📱 SMS MODE — chat-window unification (2026-05-11)
   ---------------------------------------------------------
   The LEX AI Phone tab renders the user's Twilio SMS/MMS thread inside
   the same chat window UI as the inbox DMs. The backend already double-
   writes each SMS into a "DM shadow" under:
        userConversations/{uid}/profiles/sms/inbox/{cid}/messages/{mid}
   These helpers wrap subscribe / send / mark-read / react against that
   path so the UI doesn't have to know the difference from regular DMs.

   v1 constraints:
     - 1:1 only (no group MMS) — peer is identified by E.164 phone.
     - "Edit" is unsupported (Twilio MMS is immutable on the carrier side).
     - Reactions are "web-only": stored in Firestore at
        messages/{mid}.reactionsWebOnly.{emoji} = [uid, ...]
       and NEVER pushed to Twilio. The reaction picker should show a
       "web-only" hint so users know the recipient's phone won't see it.

   peerUid for the chat UI is the synthetic id `__sms__{phoneE164}`. The
   UI renders this as a "phone chip" instead of looking up a real user.
   ========================================================= */

/** Fixed bucket name for the phone tab's SMS thread (one per user). */
export const SMS_PROFILE_ID = "sms";

/**
 * Deterministic cid for an SMS thread. Mirrors backend lib/dmShadow.js
 * exactly — keep these two in sync.
 */
export const pairCidSms = (ownerUid, peerPhoneE164) => {
  const u = cleanStr(ownerUid);
  const p = cleanStr(peerPhoneE164).replace(/^\+/, "");
  if (!u || !p) throw new Error("pairCidSms: uid/phone missing");
  return `c_sms_${u}_${p}`;
};

/**
 * Subscribe to an SMS thread (messages + thread head). Same shape as
 * LXC_subscribeConversation so the chat window can use it interchangeably.
 *
 * @param {{uid:string, cid:string}} arg
 * @param {Function} setState
 * @param {object} [opts]   { pageSize?: number, after?: any }
 * @returns {Function} unsubscribe
 */
export function LXC_subscribeSmsConversation(arg1, setState, opts = {}) {
  if (!arg1 || typeof arg1 !== "object") return () => {};
  const uid = cleanStr(arg1.uid);
  const cid = normalizeRid(arg1.cid);
  if (!uid || !cid || typeof setState !== "function") return () => {};
  return LXC_subscribeConversation(
    { uid, myProfileId: SMS_PROFILE_ID, cid },
    setState,
    opts
  );
}

/**
 * Send an SMS / MMS through the chat composer. Calls the backend chat-send
 * endpoint, which (a) routes through Twilio, (b) mirrors the bubble into
 * the DM shadow so onSnapshot picks it up.
 *
 * The optimistic UI write is intentionally deferred to the server-side
 * shadow write — the network roundtrip is fast enough (< 1s in practice)
 * and avoiding a local "pending" bubble keeps the data model single-
 * sourced from Firestore.
 *
 * @param {object} args
 * @param {string} args.idToken       - Firebase ID token (await getIdToken()).
 * @param {string} args.toPhoneE164   - Recipient phone in E.164.
 * @param {string} [args.text]        - Body. Empty allowed iff mediaUrls.length > 0.
 * @param {string[]} [args.mediaUrls] - Optional MMS (https URLs, ≤10).
 * @param {string} [args.apiBase]     - Defaults to "" (same-origin).
 * @returns {Promise<{ok:boolean, sid?:string, cid?:string, mid?:string, error?:string}>}
 */
export async function LXC_sendSms({
  idToken,
  toPhoneE164,
  text = "",
  mediaUrls = [],
  apiBase = "",
} = {}) {
  const token = cleanStr(idToken);
  const to = cleanStr(toPhoneE164);
  if (!token) return { ok: false, error: "NO_AUTH" };
  if (!/^\+[1-9]\d{6,14}$/.test(to)) return { ok: false, error: "BAD_TO_PHONE" };

  const body = {
    toPhoneE164: to,
    text: String(text || "").slice(0, 1600),
    mediaUrls: Array.isArray(mediaUrls) ? mediaUrls.slice(0, 10) : [],
  };

  const url = `${cleanStr(apiBase)}/api/sms/chat-send`;
  let resp;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return { ok: false, error: "NETWORK_FAILED" };
  }
  let json = {};
  try {
    json = (await resp.json()) || {};
  } catch {
    /* empty body */
  }
  if (!resp.ok || json.ok === false) {
    return { ok: false, error: String(json.error || `HTTP_${resp.status}`) };
  }
  return { ok: true, sid: json.sid || "", cid: json.cid || "", mid: json.mid || "" };
}

/**
 * Toggle a "web-only" reaction on an SMS message. Stored separately from
 * the standard `reactions` field so we can't accidentally surface it as
 * something the recipient's phone saw (they didn't — Twilio doesn't carry
 * reactions).
 *
 * Schema: messages/{mid}.reactionsWebOnly.{emoji} = [uid, uid, ...]
 *
 * @param {object} args
 * @param {string} args.uid
 * @param {string} args.cid
 * @param {string} args.messageId
 * @param {string} args.emoji
 * @param {boolean} [args.add=true]
 * @returns {Promise<boolean>}
 */
export async function LXC_toggleSmsReaction({
  uid,
  cid,
  messageId,
  emoji,
  add = true,
} = {}) {
  const u = cleanStr(uid);
  const c = normalizeRid(cid);
  const mid = cleanStr(messageId);
  const e = String(emoji || "").trim();
  if (!u || !c || !mid || !e) return false;

  const op = add ? arrayUnion(u) : arrayRemove(u);
  const path = `reactionsWebOnly.${e}`;
  const ref = doc(messagesCol(u, SMS_PROFILE_ID, c), mid);
  try {
    await updateDoc(ref, { [path]: op });
  } catch (_err) {
    try {
      await setDoc(
        ref,
        { reactionsWebOnly: { [e]: add ? [u] : [] } },
        { merge: true }
      );
    } catch (err2) {
      warn("[toggleSmsReaction] failed", { err: err2?.message || err2 });
      return false;
    }
  }
  dbg("[toggleSmsReaction]", { mid, emoji: e, add });
  return true;
}

/**
 * Mark an SMS thread as read. Same as LXC_markReadInboxRow but with the
 * SMS profile id hardcoded so callers don't have to remember it.
 */
export async function LXC_markSmsThreadRead(uid, cid) {
  return LXC_markReadInboxRow(uid, SMS_PROFILE_ID, cid);
}

/**
 * Watch the user's SMS inbox list (typically just one row in v1, but the
 * shape is ready for multi-peer SMS in v2). Same callback signature as
 * LXC_listMyConversations.
 */
export function LXC_listSmsConversations(uid, opts = {}) {
  return LXC_listMyConversations(uid, {
    ...opts,
    myProfileId: SMS_PROFILE_ID,
  });
}

/* =========================================================
   ✅ debug helper: seed message (Scheme B)
   ========================================================= */
export async function LXC_seedFakeMessage(myUid, targetUid, text = "hello(seed)", opts = {}) {
  const cid = await LXC_openOrCreateConversation(myUid, targetUid, opts);
  const from = cleanStr(myUid);
  const to = cleanStr(targetUid);

  // you MUST pass profileIds in opts.meta for Scheme B
  await LXC_sendMessage(cid, {
    text,
    from,
    to,
    context: opts?.context || null,
    meta: opts?.meta || null,
  });

  info("[seed][schemeB] message", { cid, from, to, text });
  return cid;
}
